import React from 'react';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  brandFonts,
  fontSize,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Container, Link } from './ui';

const StyledMegaMenu = styled.div`
  ${minBreakpointQuery.mlarge`
    position: absolute;
    padding-top: 25px;
    color: ${brandColours.primary};
  `}
`;

const StyledContainer = styled(Container)`
  max-width: 1300px;
`;

const StyledInner = styled.div`
  ${minBreakpointQuery.mlarge`
    padding: 40px 50px;
    background-color: ${standardColours.white};
    border-radius: 0 0 10px 10px;
    box-shadow: 0 4px 8px 0 ${standardColours.transparentBlack(0.1)};
  `}

  ${minBreakpointQuery.large`
    padding: 50px 60px;
  `}
`;

const StyledList = styled.ul`
  ${minBreakpointQuery.mlarge`
    display: grid;
    grid-template-columns: repeat(${({ itemsNum }) => itemsNum}, 1fr);
    gap: 40px;
  `}

  ${minBreakpointQuery.large`
    gap: 50px;
  `}
`;

const StyledItem = styled.li`
  > span {
    ${maxBreakpointQuery.mlarge`
      ${visuallyHidden()};
    `}

    ${minBreakpointQuery.mlarge`
      color: ${brandColours.quinary};
      ${fontSize(14)};
    `}
  }
`;

const StyledSubList = styled.ul`
  ${maxBreakpointQuery.mlarge`
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-top: 20px;
    padding: 20px 0;
    column-count: ${({ itemsNum }) => (itemsNum === 1 ? 4 : 2)};
    column-gap: 20px;
    border-top: 1px solid ${greyColours.lightGrey};
  `}
`;

const StyledSubItem = styled.li`
  ${maxBreakpointQuery.mlarge`
    margin-top: 6px;
  `}

  &:first-child {
    ${maxBreakpointQuery.mlarge`
      margin-top: 0;
    `}
  }
`;

const StyledSubLink = styled(Link)`
  ${maxBreakpointQuery.mlarge`
    padding: 2px 0;
    color: ${standardColours.white};
    ${fontSize(20)};
    text-decoration: underline;
    text-decoration-thickness: 1px;
  `}

  ${maxBreakpointQuery.tsmall`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.mlarge`
    position: relative;
    padding: 5px 0;
  `}

  &:before {
    ${minBreakpointQuery.mlarge`
      content: '';
      position: absolute;
      bottom: 5px;
      left: 0;
      height: 1px;
      width: 100%;
      background-color: ${brandColours.primary};
      opacity: 0;
      transition: ${standardTransition('opacity')};
    `}
  }

  > span {
    ${minBreakpointQuery.mlarge`
      position: relative;
    `}
  }

  &:hover,
  &.current-page {
    &:before {
      ${minBreakpointQuery.mlarge`
        opacity: 1;
      `}
    }
  }
`;

const StyledCta = styled.div`
  ${minBreakpointQuery.mlarge`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding: 15px 30px;
    ${fontSize(18)};
    background-color: ${brandColours.quaternary};
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
    padding: 18px 40px;
  `}
`;

const StyledCtaText = styled.p`
  ${minBreakpointQuery.mlarge`
    ${brandFonts.secondary};
  `}
`;

const StyledCtaLink = styled(Link)`
  ${minBreakpointQuery.mlarge`
    position: relative;
    margin-left: 20px;
    padding-right: 22px;
    white-space: nowrap;
  `}

  &:after {
    ${minBreakpointQuery.mlarge`
      content: '➔';
      position: absolute;
      top: 50%;
      right: 0;
      color: ${brandColours.primary};
      ${fontSize(16)};
      transform: translateY(-50%);
      transition: ${standardTransition('transform')};
    `}
  }

  &:hover {
    &:after {
      ${minBreakpointQuery.mlarge`
        transform: translateX(3px) translateY(-50%);
      `}
    }
  }
`;

const MegaMenu = ({ items, banner, ...props }) =>
  items &&
  items.length > 0 && (
    <StyledMegaMenu {...props}>
      <StyledContainer>
        <StyledInner>
          <StyledList itemsNum={items.length}>
            {items.map(({ text, subItems }, i) => (
              <StyledItem key={`megamenu-item-${i}`}>
                <span>{text}</span>
                {subItems && subItems.length > 0 && (
                  <StyledSubList itemsNum={items.length}>
                    {subItems.map(({ text, url }, i) => (
                      <StyledSubItem key={`sub-megamenu-item-${i}`}>
                        <StyledSubLink to={url} activeClassName="current-page">
                          <span>{text}</span>
                        </StyledSubLink>
                      </StyledSubItem>
                    ))}
                  </StyledSubList>
                )}
              </StyledItem>
            ))}
          </StyledList>
          {banner && (
            <StyledCta>
              <StyledCtaText>{banner.message}</StyledCtaText>
              <StyledCtaLink to={banner.linkUrl}>
                {banner.linkText}
              </StyledCtaLink>
            </StyledCta>
          )}
        </StyledInner>
      </StyledContainer>
    </StyledMegaMenu>
  );

export default MegaMenu;
